import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Space, Statistic, Tag, Typography, Empty, Skeleton, Table, message } from 'antd';
import {
    DatabaseOutlined,
    EditOutlined,
    HistoryOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { aiAdminApi, SubsystemSummary } from '../../services/ai-admin-api';
import { RelationshipDiagram } from './components/RelationshipDiagram';

const { Title, Text } = Typography;

/**
 * Hub home page. Three tiles up top, relationship diagram in the middle,
 * activity feed at the bottom. Sandbox spend is a placeholder until Phase 4
 * lands the cost-attribution backend.
 */
export const AIHubHomePage: React.FC = () => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [subsystems, setSubsystems] = useState<SubsystemSummary[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const load = useCallback(async () => {
        if (!jwt) return;
        setIsLoading(true);
        try {
            const data = await aiAdminApi.listSubsystems(jwt);
            setSubsystems(data);
        } catch (err: any) {
            message.error(err.message || 'Failed to load subsystems');
        } finally {
            setIsLoading(false);
        }
    }, [jwt]);

    useEffect(() => {
        load();
    }, [load]);

    const drafts = useMemo(
        () => subsystems.filter((s) => s.hasDraft),
        [subsystems],
    );

    const recentlyPublished = useMemo(() => {
        return [...subsystems]
            .filter((s) => s.lastPublishedAt)
            .sort((a, b) =>
                (b.lastPublishedAt ?? '').localeCompare(a.lastPublishedAt ?? ''),
            )
            .slice(0, 5);
    }, [subsystems]);

    return (
        <div>
            <Title level={2}>
                AI Administration
            </Title>
            <Text type="secondary">
                Versioned configuration for every AI subsystem on the platform.
                Edit, sandbox, publish, roll back — all from one place.
            </Text>

            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} md={8}>
                    <Card>
                        <Statistic
                            title="Pending drafts"
                            value={isLoading ? 0 : drafts.length}
                            prefix={<EditOutlined />}
                            valueStyle={{
                                color: drafts.length > 0 ? '#fa8c16' : undefined,
                            }}
                        />
                        {drafts.length > 0 && (
                            <Space wrap style={{ marginTop: 8 }}>
                                {drafts.map((d) => (
                                    <Tag key={d.id} color="orange">
                                        <Link to={`/admin/ai/${d.id}`}>{d.displayName}</Link>
                                    </Tag>
                                ))}
                            </Space>
                        )}
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card>
                        <Statistic
                            title="Sandbox spend (MTD)"
                            value="—"
                            prefix={<DollarOutlined />}
                        />
                        <Text type="secondary">
                            Available after Phase 4 sandbox ships.
                        </Text>
                    </Card>
                </Col>
                <Col xs={24} md={8}>
                    <Card title="Recent publishes" extra={<HistoryOutlined />}>
                        {isLoading ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                        ) : recentlyPublished.length === 0 ? (
                            <Empty description="No publishes yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Table
                                size="small"
                                rowKey="id"
                                showHeader={false}
                                pagination={false}
                                dataSource={recentlyPublished}
                                columns={[
                                    {
                                        dataIndex: 'displayName',
                                        render: (name: string, row: SubsystemSummary) => (
                                            <Link to={`/admin/ai/${row.id}`}>{name}</Link>
                                        ),
                                    },
                                    {
                                        dataIndex: 'currentPublishedVersion',
                                        width: 60,
                                        render: (v: number | null) => (
                                            <Tag>v{v ?? '?'}</Tag>
                                        ),
                                    },
                                    {
                                        dataIndex: 'lastPublishedAt',
                                        width: 110,
                                        render: (ts: string) => (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {ts ? new Date(ts).toLocaleDateString() : '—'}
                                            </Text>
                                        ),
                                    },
                                ]}
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            <Card
                style={{ marginTop: 24 }}
                title={
                    <span>
                        <DatabaseOutlined style={{ marginRight: 8 }} />
                        Subsystem relationship diagram
                    </span>
                }
                extra={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Click a node to open its configuration page.
                    </Text>
                }
            >
                <RelationshipDiagram subsystems={subsystems} loading={isLoading} />
            </Card>
        </div>
    );
};
